import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import WorkspaceLanding from "@/components/workspace/WorkspaceLanding";
import GenericLoader from "@/components/ui/GenericLoader";
import { useAuth } from "@/hooks/useAuth";
import useUserBrands from "@/hooks/queries/useUserBrands";

const AddNewProperty = lazy(
  () => import("@/pages/manager/workspace/pages/properties/AddNewProperty"),
);
const BrandAccommodations = lazy(
  () =>
    import("@/pages/manager/workspace/pages/accommodations/BrandAccommodations"),
);
const AddNewAccommodation = lazy(
  () =>
    import("@/pages/manager/workspace/pages/accommodations/NewAccommodation"),
);
const AddNewBrand = lazy(() => import("@/pages/manager/brand/AddNewBrand"));
const AccommodationManagement = lazy(
  () =>
    import("@/pages/manager/workspace/pages/accommodations/AccommodationManagement"),
);

// Shell Layout
const BrandDashboardLayout = lazy(
  () => import("@/layouts/BrandDashboardLayout"),
);

// Global Account Pages
const LandlordProfile = lazy(
  () => import("@/components/workspace/LandlordProfile"),
);

// Brand & Portfolio Level Pages
const LandlordDashboard = lazy(
  () => import("@/pages/manager/workspace/pages/Dashboard"),
);
const WorkspaceProperties = lazy(
  () => import("@/pages/manager/workspace/pages/properties/Properties"),
);
const PropertyManagement = lazy(
  () => import("@/pages/manager/workspace/pages/properties/PropertyManagement"),
);
const LandlordAnalytics = lazy(
  () => import("@/pages/manager/workspace/pages/analytics/Analytics"),
);
const DomainManagement = lazy(
  () => import("@/pages/manager/workspace/pages/setting/cmps/DomainManagement"),
);
const BrandWebsiteSettings = lazy(
  () => import("@/pages/manager/workspace/pages/website/BrandWebsiteSettings"),
);
const WorkspaceSettings = lazy(
  () => import("@/pages/manager/workspace/pages/setting/WorkspaceSettings"),
);
const WorkspaceBilling = lazy(
  () => import("@/pages/manager/workspace/pages/billing/WorkspaceBilling"),
);

// Property-Specific Level Pages
const LandlordBooking = lazy(
  () => import("@/pages/manager/workspace/pages/booking/Booking"),
);
const Reviews = lazy(
  () => import("@/pages/manager/workspace/pages/reviews/Reviews"),
);
const Upload = lazy(
  () => import("@/pages/manager/workspace/pages/uploads/Upload"),
);
const UploadAccommodation = lazy(
  () =>
    import("@/pages/manager/workspace/pages/uploads/accommodations/UploadAccommodation"),
);

// Standalone Deletion Flows
const PropertyDeletion = lazy(
  () => import("@/pages/manager/utils/deletion/WorkspaceAccountDeletion"),
);
const PropertyDeletionAlternative = lazy(
  () => import("@/pages/manager/utils/deletion/PropertyDeletionAlternative"),
);

export default function WorkspaceRoutes() {
  const chooserPath = "chooser";
  const { isAuthenticated, user } = useAuth();
  const { data: brands = [], isPending: isLoadingBrands } = useUserBrands();
  const defaultPath =
    isAuthenticated && !isLoadingBrands && brands.length
      ? brands?.filter((b) => b.ownerUserId === user?.id)?.[0]?.slug ||
        brands?.[0]?.slug
      : chooserPath;
  return (
    <Suspense fallback={<GenericLoader type={"data"} message={"Preparing your workspace"} />}>
      <Routes>
        {/* Global User Profile (Not scoped to any brand) */}
        <Route path="profile" element={<LandlordProfile />} />
        <Route path={chooserPath} element={<WorkspaceLanding />} />
        <Route path="chooser/new-brand" element={<AddNewBrand />} />

        {/* Root Workspace Fallback / Redirect */}
        <Route index element={<Navigate to={defaultPath} replace />} />

        {/* ------------------------------------------------------------- */}
        {/* MAIN WORKSPACE SHELL: /:brandSlug                   */}
        {/* ------------------------------------------------------------- */}
        <Route path=":brandSlug" element={<BrandDashboardLayout />}>
          {/* --- BRAND LEVEL SCOPE --- */}
          {/* Overview Dashboard for all properties under this brand */}
          <Route index element={<LandlordDashboard />} />

          {/* List of all properties under this brand */}
          <Route path="properties/*">
            <Route index element={<WorkspaceProperties />} />
            <Route path="new" element={<AddNewProperty />} />
            <Route path=":propertySlug" element={<PropertyManagement />} />
          </Route>
          {/* List of all properties under this brand */}
          <Route path="accommodations/*">
            <Route index element={<BrandAccommodations />} />
            <Route path="new" element={<AddNewAccommodation />} />
            <Route
              path=":accommodationSlug"
              element={<AccommodationManagement />}
            />
          </Route>

          {/* Aggregate analytics for the entire brand portfolio */}
          <Route path="analytics" element={<LandlordAnalytics />} />

          {/* Brand domain, public layout, and hero customization */}
          <Route path="website" element={<BrandWebsiteSettings />} />
          <Route path="domains" element={<DomainManagement />} />

          {/* Brand workspace settings (Team, payouts, gateway) */}
          <Route path="settings" element={<WorkspaceSettings />} />

          {/* Brand billing overview */}
          <Route path="billing" element={<WorkspaceBilling />} />

          {/* --- PROPERTY LEVEL SCOPE: /workspace/:brandSlug/:propertySlug --- */}
          <Route path=":propertySlug">
            {/* Property Overview */}
            <Route index element={<WorkspaceProperties />} />

            {/* Property Bookings */}
            <Route path="bookings" element={<LandlordBooking />} />

            {/* Property Reviews */}
            <Route path="reviews" element={<Reviews />} />

            {/* Edit / Upload Property Info */}
            <Route path="uploads" element={<Upload />} />
            <Route
              path="uploads/accommodation"
              element={<UploadAccommodation />}
            />
          </Route>
        </Route>

        {/* ------------------------------------------------------------- */}
        {/* STANDALONE FLOWS */}
        {/* Deletion flows (Outside main workspace shell) */}
        {/* ------------------------------------------------------------- */}

        <Route path="account/delete" element={<PropertyDeletion />} />
        <Route
          path="account/delete/alternative"
          element={<PropertyDeletionAlternative />}
        />
      </Routes>
    </Suspense>
  );
}
